/**
 * Layout Service
 *
 * Centralized layout calculations that account for all UI elements.
 * This service ensures draggable panels and UI components are positioned
 * correctly relative to sidebars, toolbars, and other fixed elements.
 *
 * @module utils/layoutService
 */

import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

/**
 * LayoutService class for managing viewport calculations
 */
export class LayoutService {
  constructor(store) {
    this.store = store;

    // Cache for performance
    this._cachedViewport = null;
    this._cacheTimestamp = 0;
    this._cacheDuration = 100; // Cache for 100ms
  }

  /**
   * Get available viewport dimensions accounting for sidebars and fixed elements
   *
   * @returns {{left: number, top: number, right: number, bottom: number, width: number, height: number}}
   */
  getAvailableViewport() {
    // Check cache
    const now = Date.now();
    if (this._cachedViewport && (now - this._cacheTimestamp) < this._cacheDuration) {
      return this._cachedViewport;
    }

    const leftSidebarWidth = this.getLeftSidebarWidth();
    const rightSidebarWidth = 0; // Reserved for future right sidebar
    const topBarHeight = 64; // Approximate top navigation bar height
    const bottomPadding = 20; // Bottom padding for safety

    const viewport = {
      left: leftSidebarWidth,
      top: topBarHeight,
      right: window.innerWidth - rightSidebarWidth,
      bottom: window.innerHeight - bottomPadding,
      width: window.innerWidth - leftSidebarWidth - rightSidebarWidth,
      height: window.innerHeight - topBarHeight - bottomPadding
    };

    // Cache the result
    this._cachedViewport = viewport;
    this._cacheTimestamp = now;

    return viewport;
  }

  /**
   * Calculate left sidebar width based on current state
   *
   * The left sidebar can be in different states:
   * - Display settings visible: Full width panel (responsive: 420px, 380px, 340px, or 300px)
   * - Regular tab visible: Standard panel width (300px)
   * - Collapsed: Just the icon bar (60px)
   *
   * @returns {number} Current sidebar width in pixels
   */
  getLeftSidebarWidth() {
    if (!this.store) {
      // Fallback if store not available
      return 60;
    }

    try {
      const isDisplaySettingsVisible = this.store.getters['GET_IS_DISPLAY_SETTINGS_VISIBLE'];
      const isTabShown = this.store.state.isTabShown;

      if (isDisplaySettingsVisible) {
        // Display settings panel - responsive width based on viewport
        const width = window.innerWidth;
        if (width >= 1440) return 420;
        if (width >= 1024) return 380;
        if (width >= 768) return 340;
        return 300;
      }

      if (isTabShown) {
        return 300; // Regular tab panel width
      }

      return 60; // Collapsed sidebar width (just icons)
    } catch (error) {
      debugWarn('LayoutService', 'Error getting sidebar width:', error);
      return 60; // Safe fallback
    }
  }

  /**
   * Calculate constrained position for draggable panel
   * Ensures panel stays within visible viewport boundaries
   *
   * @param {number} x - Desired X position
   * @param {number} y - Desired Y position
   * @param {number} panelWidth - Panel width in pixels
   * @param {number} panelHeight - Panel height in pixels
   * @param {number} margin - Minimum margin from edges (default: 20)
   * @returns {{x: number, y: number}} Constrained position
   */
  constrainPanelPosition(x, y, panelWidth, panelHeight, margin = 20) {
    const viewport = this.getAvailableViewport();

    // Calculate boundaries
    const maxX = viewport.right - panelWidth - margin;
    const maxY = viewport.bottom - panelHeight - margin;
    const minX = viewport.left + margin;
    const minY = viewport.top + margin;

    // Constrain position
    const constrainedX = Math.max(minX, Math.min(maxX, x));
    const constrainedY = Math.max(minY, Math.min(maxY, y));

    return {
      x: constrainedX,
      y: constrainedY
    };
  }

  /**
   * Get default position for a panel (right-aligned, accounting for sidebar)
   *
   * @param {number} panelWidth - Panel width in pixels
   * @param {number} panelHeight - Panel height in pixels
   * @param {number} offsetFromRight - Distance from right edge (default: 20)
   * @param {number} offsetFromTop - Distance from top (default: 100)
   * @returns {{x: number, y: number}} Default panel position
   */
  getDefaultPanelPosition(panelWidth, panelHeight, offsetFromRight = 20, offsetFromTop = 100) {
    const viewport = this.getAvailableViewport();

    return {
      x: viewport.right - panelWidth - offsetFromRight,
      y: viewport.top + offsetFromTop
    };
  }

  /**
   * Check if a saved position is still valid (sidebar might have changed size)
   *
   * A position is considered valid if at least 50% of the panel is visible
   * within the current viewport boundaries.
   *
   * @param {number} x - Saved X position
   * @param {number} y - Saved Y position
   * @param {number} panelWidth - Panel width
   * @param {number} panelHeight - Panel height
   * @returns {boolean} True if position is still valid
   */
  isPositionValid(x, y, panelWidth, panelHeight) {
    const viewport = this.getAvailableViewport();

    // Calculate panel boundaries
    const panelRight = x + panelWidth;
    const panelBottom = y + panelHeight;

    // Calculate visible area
    const visibleLeft = Math.max(x, viewport.left);
    const visibleTop = Math.max(y, viewport.top);
    const visibleRight = Math.min(panelRight, viewport.right);
    const visibleBottom = Math.min(panelBottom, viewport.bottom);

    // Calculate visible dimensions
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    // Calculate areas
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = panelWidth * panelHeight;

    // Position is valid if at least 50% visible
    const isValid = visibleArea >= totalArea * 0.5;

    if (!isValid) {
      debugLog('LayoutService', 'Position invalid:', {
        x, y,
        panelSize: { width: panelWidth, height: panelHeight },
        visibleArea,
        totalArea,
        visiblePercentage: ((visibleArea / totalArea) * 100).toFixed(1) + '%',
        viewport
      });
    }

    return isValid;
  }

  /**
   * Get safe position for a panel, adjusting if current position is invalid
   *
   * @param {number} currentX - Current X position
   * @param {number} currentY - Current Y position
   * @param {number} panelWidth - Panel width
   * @param {number} panelHeight - Panel height
   * @returns {{x: number, y: number, adjusted: boolean}} Safe position and whether it was adjusted
   */
  getSafePosition(currentX, currentY, panelWidth, panelHeight) {
    if (this.isPositionValid(currentX, currentY, panelWidth, panelHeight)) {
      return {
        x: currentX,
        y: currentY,
        adjusted: false
      };
    }

    // Position is invalid, get default position
    const defaultPos = this.getDefaultPanelPosition(panelWidth, panelHeight);

    debugLog('LayoutService', 'Adjusted invalid position:', {
      from: { x: currentX, y: currentY },
      to: defaultPos
    });

    return {
      ...defaultPos,
      adjusted: true
    };
  }

  /**
   * Calculate position for centering a panel
   *
   * @param {number} panelWidth - Panel width
   * @param {number} panelHeight - Panel height
   * @returns {{x: number, y: number}} Centered position
   */
  getCenteredPosition(panelWidth, panelHeight) {
    const viewport = this.getAvailableViewport();

    return {
      x: viewport.left + (viewport.width - panelWidth) / 2,
      y: viewport.top + (viewport.height - panelHeight) / 2
    };
  }

  /**
   * Get responsive panel dimensions based on viewport size
   *
   * @param {number} desiredWidth - Desired panel width
   * @param {number} desiredHeight - Desired panel height
   * @param {number} maxWidthPercent - Maximum width as percentage of viewport (default: 90)
   * @param {number} maxHeightPercent - Maximum height as percentage of viewport (default: 80)
   * @returns {{width: number, height: number}} Responsive dimensions
   */
  getResponsivePanelSize(desiredWidth, desiredHeight, maxWidthPercent = 90, maxHeightPercent = 80) {
    const viewport = this.getAvailableViewport();

    const maxWidth = viewport.width * (maxWidthPercent / 100);
    const maxHeight = viewport.height * (maxHeightPercent / 100);

    return {
      width: Math.min(desiredWidth, maxWidth),
      height: Math.min(desiredHeight, maxHeight)
    };
  }

  /**
   * Clear cached viewport data
   * Call this when sidebar state changes
   */
  clearCache() {
    this._cachedViewport = null;
    this._cacheTimestamp = 0;
  }

  /**
   * Subscribe to store changes and clear cache when sidebar changes
   *
   * @param {Function} callback - Optional callback to invoke when layout changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToLayoutChanges(callback) {
    if (!this.store) return () => {};

    // Watch for sidebar state changes
    return this.store.subscribe((mutation) => {
      // Clear cache when sidebar-related state changes
      if (
        mutation.type === 'setIsTabShown' ||
        mutation.type === 'SET_IS_DISPLAY_SETTINGS_VISIBLE' ||
        mutation.type === 'setSelectedTab'
      ) {
        this.clearCache();

        if (callback) {
          callback({
            mutation: mutation.type,
            viewport: this.getAvailableViewport()
          });
        }
      }
    });
  }
}

// Singleton instance
let layoutServiceInstance = null;

/**
 * Get LayoutService singleton instance
 *
 * @param {Object} store - Vuex store instance
 * @returns {LayoutService} LayoutService instance
 */
export function getLayoutService(store) {
  if (!layoutServiceInstance) {
    layoutServiceInstance = new LayoutService(store);
  }
  return layoutServiceInstance;
}

/**
 * Reset singleton instance (useful for testing)
 */
export function resetLayoutService() {
  layoutServiceInstance = null;
}

// Export for convenience
export default {
  LayoutService,
  getLayoutService,
  resetLayoutService
};
